import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import IosShareIcon from "@mui/icons-material/IosShare";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import logoMark from "../../assets/images/logos/logo_mark_single.webp";
import {
	clearDeferredInstallPrompt,
	getDeferredInstallPrompt,
	subscribeInstallPrompt,
} from "./deferredPrompt";
import {
	armContinueOnce,
	goToPendingOrFallback,
	getPendingPlatform,
	markInstallSkipped,
	markShellInstalled,
} from "./continueAfterAuth";
import {
	supportsPwaProtocolLaunch,
	tryLaunchInstalledPwa,
} from "./openInInstalledApp";
import { isStandalone } from "./isStandalone";
import { resolveInstalledState } from "./isInstalled";
import { PWA_SHELL_STORAGE } from "./config";

/**
 * Post-auth install gate.
 *
 * Route: /pwa/install
 * Platform is read from pending storage (psl/biz), not from the URL.
 *
 * CTAs are mutually distinct:
 * - Install Pofsis App → beforeinstallprompt (HTTPS install UI)
 * - Open in App → web+pofsis:// protocol deep-link (desktop Chromium best-effort)
 * - Continue in Browser → HTTPS goToPendingOrFallback (never claimed as PWA launch)
 *
 * After a fresh install (or when this window becomes standalone), navigate to the
 * pending tool URL instead of showing Open in App.
 *
 * No automatic HTTPS redirect after a protocol attempt.
 */
export const InstallPage = () => {
	const platform = getPendingPlatform() || "psl";
	const [installPrompt, setInstallPrompt] = useState(getDeferredInstallPrompt());
	const [installing, setInstalling] = useState(false);
	const [appearsInstalled, setAppearsInstalled] = useState(false);
	const [continuing, setContinuing] = useState(false);
	const [protocolSupported] = useState(() => supportsPwaProtocolLaunch());
	const continuedRef = useRef(false);

	useEffect(() => {
		const continueToPending = () => {
			if (continuedRef.current) return;
			continuedRef.current = true;
			setContinuing(true);
			markShellInstalled();
			armContinueOnce();
			clearDeferredInstallPrompt();
			setInstalling(false);
			goToPendingOrFallback(platform);
		};

		if (isStandalone()) {
			continueToPending();
			return undefined;
		}

		const unsubscribe = subscribeInstallPrompt((prompt) => {
			setInstallPrompt(prompt);
			if (prompt) setAppearsInstalled(false);
		});

		// Fresh install in this tab: go to the tool URL (do not flip to Open in App).
		const onInstalled = () => {
			continueToPending();
		};

		window.addEventListener("appinstalled", onInstalled);

		// navigate-existing can switch display-mode without remounting React.
		const standaloneMq =
			typeof window.matchMedia === "function"
				? window.matchMedia("(display-mode: standalone)")
				: null;
		const onDisplayModeChange = (event) => {
			if (event.matches) continueToPending();
		};
		if (standaloneMq) {
			if (typeof standaloneMq.addEventListener === "function") {
				standaloneMq.addEventListener("change", onDisplayModeChange);
			} else if (typeof standaloneMq.addListener === "function") {
				standaloneMq.addListener(onDisplayModeChange);
			}
		}

		let cancelled = false;
		let pollId = null;
		let pollStopId = null;

		const detect = async () => {
			await new Promise((r) => setTimeout(r, 400));
			if (cancelled || continuedRef.current) return;

			if (isStandalone()) {
				continueToPending();
				return;
			}

			const promptNow = getDeferredInstallPrompt();
			const installed = await resolveInstalledState({
				installPromptAvailable: Boolean(promptNow),
			});
			if (cancelled || continuedRef.current) return;

			// Already installed in a normal browser tab — offer Open in App.
			if (installed) setAppearsInstalled(true);
		};

		detect();

		// Short poll in case appinstalled / display-mode change are late.
		pollId = window.setInterval(() => {
			if (cancelled || continuedRef.current) return;
			if (isStandalone()) {
				window.clearInterval(pollId);
				pollId = null;
				continueToPending();
			}
		}, 500);
		pollStopId = window.setTimeout(() => {
			if (pollId != null) {
				window.clearInterval(pollId);
				pollId = null;
			}
		}, 8000);

		return () => {
			cancelled = true;
			unsubscribe();
			window.removeEventListener("appinstalled", onInstalled);
			if (standaloneMq) {
				if (typeof standaloneMq.removeEventListener === "function") {
					standaloneMq.removeEventListener("change", onDisplayModeChange);
				} else if (typeof standaloneMq.removeListener === "function") {
					standaloneMq.removeListener(onDisplayModeChange);
				}
			}
			if (pollId != null) window.clearInterval(pollId);
			if (pollStopId != null) window.clearTimeout(pollStopId);
		};
	}, [platform]);

	const continueInBrowser = () => {
		if (continuedRef.current) return;
		continuedRef.current = true;
		setContinuing(true);
		markInstallSkipped();
		goToPendingOrFallback(platform);
	};

	const openInApp = () => {
		tryLaunchInstalledPwa(platform);
		// No automatic HTTPS redirect — user must click Continue in Browser.
	};

	const addToHomeScreen = async () => {
		const promptEvent = installPrompt || getDeferredInstallPrompt();
		if (!promptEvent) return;

		try {
			setInstalling(true);
			await promptEvent.prompt();
			const result = await promptEvent.userChoice;

			if (result.outcome === "accepted") {
				localStorage.setItem(
					PWA_SHELL_STORAGE.installed,
					JSON.stringify({
						accepted: true,
						at: new Date().toISOString(),
					})
				);
				// Arm resume before Chrome opens the PWA window at "/".
				armContinueOnce();
			} else {
				setInstalling(false);
			}

			clearDeferredInstallPrompt();
			setInstallPrompt(null);
		} catch (error) {
			console.error("POFSIS install failed:", error);
			setInstalling(false);
		}
	};

	if (continuing) {
		return (
			<Box
				minHeight="100vh"
				width="100%"
				display="flex"
				alignItems="center"
				justifyContent="center"
				px={2}
				sx={{
					background:
						"linear-gradient(135deg, #d8f3e8 0%, #f7fbf9 42%, #ffffff 100%)",
				}}
			>
				<Typography sx={{ color: "#64748b", fontSize: 15 }}>
					Opening…
				</Typography>
			</Box>
		);
	}

	const canInstall = Boolean(installPrompt) && !installing;
	// Best-effort: UI says "appears installed", not certainty
	const showOpenInApp = appearsInstalled && protocolSupported;
	const showInstall = !appearsInstalled;
	const showUnsupportedHint = appearsInstalled && !protocolSupported;

	return (
		<Box
			minHeight="100vh"
			width="100%"
			display="flex"
			alignItems="center"
			justifyContent="center"
			px={2}
			sx={{
				background:
					"linear-gradient(135deg, #d8f3e8 0%, #f7fbf9 42%, #ffffff 100%)",
			}}
		>
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				textAlign="center"
				width="100%"
				maxWidth={360}
			>
				<Box
					component="img"
					src={logoMark}
					alt="POFSIS"
					width={88}
					height={88}
					sx={{
						borderRadius: 3,
						boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
						objectFit: "cover",
						mb: 3,
					}}
				/>

				<Typography
					component="h1"
					sx={{
						fontWeight: 700,
						fontSize: { xs: 22, sm: 26 },
						color: "#0f172a",
						lineHeight: 1.25,
						mb: 1.25,
					}}
				>
					{showOpenInApp
						? "POFSIS appears to be installed"
						: showUnsupportedHint
							? "Continue to your destination"
							: "Install the app for instant access"}
				</Typography>

				<Typography
					sx={{
						fontSize: 14,
						color: "#64748b",
						lineHeight: 1.5,
						mb: 3.5,
						maxWidth: 320,
					}}
				>
					{showOpenInApp
						? "Open the installed POFSIS app to continue, or stay in this browser."
						: showUnsupportedHint
							? "Open the installed app from your browser or device launcher if available, or continue here."
							: "Get a faster, full-screen desktop experience with offline support."}
				</Typography>

				{showOpenInApp ? (
					<>
						<Button
							fullWidth
							variant="contained"
							disableElevation
							onClick={openInApp}
							sx={{
								mb: 1.5,
								py: 1.35,
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								fontSize: 15,
								bgcolor: "#2563EB",
								"&:hover": { bgcolor: "#1d4ed8" },
							}}
						>
							Open in App
						</Button>
						<Typography sx={{ fontSize: 13, color: "#64748b", mb: 1 }}>
							Having trouble opening the app?
						</Typography>
					</>
				) : showInstall ? (
					<Button
						fullWidth
						variant="contained"
						disableElevation
						disabled={!canInstall}
						onClick={addToHomeScreen}
						sx={{
							mb: 1.5,
							py: 1.35,
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							fontSize: 15,
							bgcolor: "#2563EB",
							"&:hover": { bgcolor: "#1d4ed8" },
							"&.Mui-disabled": {
								bgcolor: "#93c5fd",
								color: "#fff",
							},
						}}
					>
						{installing ? "Installing…" : "Install Pofsis App"}
					</Button>
				) : null}

				{showUnsupportedHint && (
					<Typography
						sx={{ fontSize: 13, color: "#64748b", mb: 1.5, maxWidth: 320 }}
					>
						Open the installed app from your browser or device launcher if
						available.
					</Typography>
				)}

				<Button
					fullWidth
					variant="outlined"
					onClick={continueInBrowser}
					sx={{
						py: 1.35,
						borderRadius: 2,
						textTransform: "none",
						fontWeight: 600,
						fontSize: 15,
						color: "#0f172a",
						borderColor: "#e2e8f0",
						bgcolor: "#fff",
						"&:hover": {
							borderColor: "#cbd5e1",
							bgcolor: "#f8fafc",
						},
					}}
				>
					Continue in Browser
				</Button>

				{showInstall && (
					<Box mt={4} width="100%" textAlign="left">
						<Typography
							sx={{ fontSize: 13, fontWeight: 600, color: "#334155", mb: 1 }}
						>
							How to add manually:
						</Typography>
						<Box
							component="ul"
							sx={{
								m: 0,
								pl: 2.25,
								color: "#64748b",
								fontSize: 13,
								lineHeight: 1.7,
							}}
						>
							<li>
								<strong>iOS:</strong> Tap Share{" "}
								<IosShareIcon
									sx={{ fontSize: 14, verticalAlign: "text-bottom" }}
								/>{" "}
								→ Select &quot;Add to Home Screen&quot;
							</li>
							<li>
								<strong>Android:</strong> Tap Menu{" "}
								<MoreVertIcon
									sx={{ fontSize: 14, verticalAlign: "text-bottom" }}
								/>{" "}
								→ Select &quot;Install Pofsis App&quot;
							</li>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default InstallPage;
